import { useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import { Alert, Button, Checkbox, Divider, Flex, Form, Typography } from 'antd';

import SectionMessage from 'components/SectionMessage/SectionMessage';
import { SETTLEMENTS_CADENCE_VALUES, SIZE, STATUS } from 'utilities/constants';

import EftaPaymentScheduleReviewTable from './EftaPaymentScheduleReviewTable';
import { useRecoveriesState } from './recoveriesState';
import { paymentsExceedOrUnderBalanceAlert } from './settlementPaymentUtils';

const { Item } = Form;
const { Text, Title, Paragraph } = Typography;

const EFTAForm = ({
  eftaDetails,
  onFinish,
  mutation,
  resetErrorMessage,
  resetSuccessMessage,
  paymentPlanError = [],
}) => {
  const { offer, paymentPlan, payments } = useRecoveriesState();

  const [emptyPaymentPlanError, setEmptyPaymentPlanError] = paymentPlanError;

  const [eftaForm] = Form.useForm();

  const { cardLast4, customerName, email, fundingAccountLast4 } = eftaDetails;
  const today = dayjs().format('L');

  const oneTimeDebit =
    paymentPlan?.cadence === SETTLEMENTS_CADENCE_VALUES.ONE_TIME_DEBIT;
  const lumpSumPayment =
    paymentPlan?.cadence === SETTLEMENTS_CADENCE_VALUES.ONE_TIME ||
    paymentPlan?.current?.length === 1;

  const oneTimeOrPaymentPlan =
    oneTimeDebit || lumpSumPayment ? 'a payment' : 'payments';

  const achOrDebitText = oneTimeDebit ? (
    <Text>
      <strong>make a one time electronic debit</strong> from a debit card for
      the following amount:
    </Text>
  ) : (
    <Text>
      <strong>
        make {lumpSumPayment ? 'an' : 'a recurring'} electronic debit
      </strong>{' '}
      from the bank account ending in {fundingAccountLast4} for the following{' '}
      {lumpSumPayment ? 'date and amount' : 'dates and amounts'}:
    </Text>
  );

  const paymentsBalanceAlert = useMemo(() => {
    return paymentsExceedOrUnderBalanceAlert({ offer, paymentPlan, payments });
  }, [
    offer?.balance,
    paymentPlan?.updatedFuture,
    paymentPlan?.current,
    payments,
  ]);

  const onConfirm = async () => {
    await eftaForm.validateFields();

    onFinish();
  };

  useEffect(() => {
    return () => {
      if (emptyPaymentPlanError) {
        setEmptyPaymentPlanError('');
        return;
      }

      if (resetErrorMessage && resetSuccessMessage) {
        resetErrorMessage('');
        resetSuccessMessage('');
      }

      eftaForm.setFields([{ name: 'efta', value: undefined }]);
    };
  }, []);

  return (
    <Flex vertical={true}>
      <Title level={5}>Read EFTA</Title>
      <Alert
        banner
        type="info"
        showIcon={true}
        title={`Confirm email address before continuing: ${email}`}
        style={{ marginBottom: 'var(--spacing)' }}
      />
      <Text type="secondary">So, to confirm:</Text>
      <Form
        name="eftaForm"
        form={eftaForm}
        layout="vertical"
        onFinish={onConfirm}
      >
        <Flex vertical={true} style={{ marginTop: 'var(--spacing-sm)' }}>
          <Paragraph
            style={{
              textAlign: 'justify',
              textJustify: 'inter-word',
            }}
          >
            <Text>
              To make {oneTimeOrPaymentPlan} towards the balance on the Mission
              Lane Visa, ending in <strong>{cardLast4}</strong>, you{' '}
              {customerName}, today {today}, authorize Mission Lane to{' '}
              {achOrDebitText}
            </Text>
          </Paragraph>
          <Title level={5}>Payment Schedule</Title>
          <EftaPaymentScheduleReviewTable />

          {paymentPlan.cadence !== SETTLEMENTS_CADENCE_VALUES.ONE_TIME_DEBIT ? (
            <>
              <Divider />
              <Paragraph
                style={{
                  textAlign: 'justify',
                  textJustify: 'inter-word',
                }}
              >
                To cancel or change {oneTimeOrPaymentPlan}, call us at least one
                business day before the payment date at (888) 695-8536 from 8:30
                AM to 9:00 PM Eastern Time, Monday through Friday.
              </Paragraph>
            </>
          ) : (
            <>
              <br />
            </>
          )}

          <Item
            name="efta"
            valuePropName="checked"
            rules={[
              {
                /* eslint-disable no-unused-vars */
                validator: (_, value) => {
                  return new Promise((resolve, reject) => {
                    if (!value) {
                      reject();
                    }
                    resolve();
                  });
                },
                message:
                  'Customer or 3rd party must verbally authorize payment to continue.',
              },
            ]}
            style={{ marginBottom: 'var(--spacing-sm)' }}
          >
            <Checkbox>
              Do you authorize [this payment/these payments]? [Wait for
              response]
            </Checkbox>
          </Item>

          <Alert
            banner
            type="warning"
            showIcon
            title="Customers or 3rd parties must verbally acknowledge the above disclosure before moving forward."
            style={{ marginBottom: 'var(--spacing-sm)' }}
          />
          <Text>
            Thank you, you’ve successfully authorized {oneTimeOrPaymentPlan}.
            We’ll send a confirmation email to {email}
          </Text>
          <Button
            type="primary"
            htmlType="submit"
            disabled={paymentsBalanceAlert?.type === 'error'}
            style={{ marginTop: 'var(--spacing)' }}
            loading={mutation.loading}
          >
            Confirm
          </Button>
          {mutation.success && (
            <SectionMessage
              data-testid="efta-confirmed"
              cover={true}
              status={STATUS.SUCCESS}
              size={SIZE.MD}
              text={mutation.success}
              visible={!!mutation.success}
            />
          )}
          {mutation.error && (
            <Alert
              banner
              type="error"
              title={mutation.error}
              style={{ marginTop: 'var(--spacing-sm)' }}
            />
          )}
          {emptyPaymentPlanError && (
            <Alert
              banner
              type="error"
              title={paymentPlanError}
              style={{ marginTop: 'var(--spacing-sm)' }}
            />
          )}
          {paymentsBalanceAlert && (
            <Alert
              banner
              type={paymentsBalanceAlert.type}
              title={paymentsBalanceAlert.message}
              style={{ marginTop: 'var(--spacing-sm)' }}
            />
          )}
        </Flex>
      </Form>
    </Flex>
  );
};

export default EFTAForm;
